import {
  Apple,
  Baby,
  ChartLine,
  Database,
  Droplet,
  Milk,
  Package,
  Refrigerator,
  TriangleAlert,
  Utensils,
  ArrowRightLeft,
  type LucideIcon,
} from 'lucide-react';

import type { AdminCollectionSummary } from '@/admin/adminApi';

/**
 * Les collections de l'explorateur, telles que l'exploitant les nomme.
 *
 * La liste blanche appartient au **serveur** (`ExplorableCollection`) : ce module
 * ne l'etend pas, il lui donne un libelle, un pictogramme et un ordre — celui de
 * la maquette. Un type que le serveur rendrait et que ce module ne connait pas
 * reste affiche (sous son nom brut, a la fin) : le cacher ferait croire qu'il
 * n'existe pas.
 *
 * `hasNature` : une collection dont les lignes se distinguent par une nature
 * (biberon ou tetee, type de mouvement, categorie de lot) affiche la colonne
 * « Nature » ; les autres ne la montrent pas, pour ne pas garnir une colonne de
 * tirets.
 */
export interface ExplorerCollection {
  readonly type: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly hasNature: boolean;
}

export const COLLECTIONS: readonly ExplorerCollection[] = [
  { type: 'feeding_session', label: 'Alimentation', icon: Milk, hasNature: true },
  { type: 'pumping_session', label: 'Tirages', icon: Droplet, hasNature: false },
  { type: 'milk_batch', label: 'Lots de lait', icon: Package, hasNature: true },
  { type: 'stock_movement', label: 'Mouvements', icon: ArrowRightLeft, hasNature: true },
  { type: 'storage_location', label: 'Lieux', icon: Refrigerator, hasNature: false },
  { type: 'solid_meal', label: 'Repas solides', icon: Utensils, hasNature: false },
  { type: 'diversification_food', label: 'Aliments', icon: Apple, hasNature: false },
  { type: 'growth_measurement', label: 'Croissance', icon: ChartLine, hasNature: false },
  { type: 'food_reaction', label: 'Réactions', icon: TriangleAlert, hasNature: false },
  { type: 'baby_profile', label: 'Profil bébé', icon: Baby, hasNature: false },
];

/** Collection de ce type, ou une collection brute pour un type que le serveur est seul a connaitre. */
export const collectionFor = (type: string): ExplorerCollection =>
  COLLECTIONS.find((collection) => collection.type === type) ?? { type, label: type, icon: Database, hasNature: false };

/** Collection ouverte par defaut : la premiere de la liste. */
export const DEFAULT_COLLECTION_TYPE: string = COLLECTIONS[0]?.type ?? 'feeding_session';

/**
 * Collections a afficher : celles du serveur, dans l'ordre de la maquette, les
 * inconnues a la suite. Un compteur absent reste absent (`count: null`), jamais
 * un zero.
 */
export const orderedCollections = (
  summaries: readonly AdminCollectionSummary[] | null,
): readonly { readonly collection: ExplorerCollection; readonly count: number | null }[] => {
  if (summaries === null) {
    return COLLECTIONS.map((collection) => ({ collection, count: null }));
  }

  const byType = new Map(summaries.map((summary) => [summary.type, summary.count]));
  const known = COLLECTIONS.filter((collection) => byType.has(collection.type)).map((collection) => ({
    collection,
    count: byType.get(collection.type) ?? null,
  }));
  const unknown = summaries
    .filter((summary) => !COLLECTIONS.some((collection) => collection.type === summary.type))
    .map((summary) => ({ collection: collectionFor(summary.type), count: summary.count }));

  return [...known, ...unknown];
};
